<script>
  import { onMount, createEventDispatcher } from "svelte";

  import XIcon from "@local/assets/icons/x.svg";
  import LocationItemIcon from "@local/assets/icons/location-item.svg";
  import LocationCollectionIcon from "@local/assets/icons/location-opening.svg";
  import LocationTypeIcon from "@local/assets/icons/location-type.svg";
  import NotesIcon from "@local/assets/icons/notes.svg";
  import DimensionIcon from "@local/assets/icons/dimension-linear.svg";
  import PlusIcon from "@local/assets/icons/plus.svg";
  import MinusIcon from "@local/assets/icons/minus.svg";

  import SelectInput from "#lib/sidebar/SelectInput.svelte";
  import TextInput from "#lib/sidebar/TextInput.svelte";
  import { UX_MODE } from "#src/env.js";

  import eb from "#src/extensions/event-bus.js";

  export let customColumns;
  export let customColColumns;
  export let standardColumns;
  export let docid;

  export let showItems;
  export let showCollections;
  export let showTypes;
  export let showNotes;
  export let showDims;
  export let locationFilter;
  export let itemFilter;
  export let collectionFilter;
  export let typeFilter;
  export let noteFilter;

  const dispatch = createEventDispatcher();

  let visible = false;
  let showRecordFilters = false;
  let filterType = UX_MODE === "shpx" ? "collection" : "item";
  let showLocationFilter = false;

  $: filter = getFilter(filterType, itemFilter, collectionFilter, typeFilter, noteFilter);
  $: filterOptions = makeFilterOptions(filterType, customColumns, customColColumns);

  function getFilter(itemFilter, itemf, collf, typef, notef) {
    if (itemFilter === "item") {
      return itemf;
    } else if (itemFilter === "collection") {
      return collf;
    } else if (itemFilter === "type") {
      return typef;
    } else if (itemFilter === "document_note") {
      return notef;
    }
  }

  function makeFilterOptions(filterType, customColumns, customColColumns) {
    let sc = [{ label: "None", value: undefined }];
    let cc = [];

    if (filterType === "item") {
      sc.push({ label: "Description", value: "description" }, { label: "Scope", value: "scope" });
      cc = customColumns;
    } else if (filterType === "collection") {
      sc.push({ label: "Description", value: "description" }, { label: "Scope", value: "scope" });
      cc = customColColumns;
    } else if (filterType === "type") {
      sc.push({ label: "Name", value: "name" }, { label: "Scope", value: "scope" });
    } else if (filterType === "document_note") {
      sc.push({ label: "Title", value: "title" }, { label: "Scope", value: "scope" });
    }

    const standard = sc.map((col) => {
      if (standardColumns[col.value]?.override) {
        col.label = standardColumns[col.value].override;
      }
      return col;
    });

    return standard.concat(
      cc.map((c) => ({
        label: c.name,
        value: `custom_column_data.${c.id}`,
      })),
    );
  }

  function updateLocationFilter(prop, value) {
    let nf = locationFilter;

    if (prop === "operator") {
      if (value) {
        if (!nf) {
          nf = {
            docid,
            field: "category",
            operator: value,
            value: "",
          };
        } else {
          nf.operator = value;
        }
      } else {
        nf = null;
      }
    } else if (prop === "value") {
      nf.value = value;
    }

    locationFilter = nf;
  }

  function updateFilter(prop, value) {
    let nf;
    if (filterType === "item") {
      nf = itemFilter;
    } else if (filterType === "collection") {
      nf = collectionFilter;
    } else if (filterType === "type") {
      nf = typeFilter;
    } else if (filterType === "document_note") {
      nf = noteFilter;
    }

    if (prop === "field") {
      if (!value) {
        nf = null;
      } else {
        nf = {
          docid,
          field: value,
          operator: "is_equal_to",
          value: "",
        };
      }
    } else {
      nf[prop] = value;
    }

    if (filterType === "item") {
      itemFilter = nf;
    } else if (filterType === "collection") {
      collectionFilter = nf;
    } else if (filterType === "type") {
      typeFilter = nf;
    } else if (filterType === "document_note") {
      noteFilter = nf;
    }
  }

  function toggleVisible() {
    eb.dispatch("close-doc-panels", "filter-settings");

    visible = !visible;

    if (locationFilter) {
      showLocationFilter = true;
    }

    if (itemFilter || collectionFilter || typeFilter || noteFilter) {
      showRecordFilters = true;
    }
  }

  function closePanel(except) {
    if (except === "filter-settings") return;
    visible = false;
  }

  function toggleRecordFilters() {
    showRecordFilters = !showRecordFilters;

    if (!showRecordFilters) {
      itemFilter = null;
      collectionFilter = null;
      typeFilter = null;
      noteFilter = null;
    }
  }

  function toggleLocationFilter() {
    showLocationFilter = !showLocationFilter;

    if (!showLocationFilter) {
      locationFilter = null;
    }
  }

  onMount(() => {
    eb.on("open-doc-filter-settings", toggleVisible);
    eb.on("close-doc-panels", closePanel);

    return () => {
      eb.unsubscribe("open-doc-filter-settings", toggleVisible);
      eb.unsubscribe("close-doc-panels", closePanel);
    };
  });
</script>

{#if visible}
  <div class="w-full flex justify-end p-4 absolute z-30 pointer-events-none text-xs">
    <div class="w-72 pointer-events-auto">
      <div class="bg-white rounded border shadow-lg text-black w-full">
        <div class="p-2 flex items-center border-b">
          <div class="grow">Filters</div>
          <button on:click={toggleVisible} class="rounded p-1 hover:bg-gray-200">
            <XIcon />
          </button>
        </div>
        <div class="px-4 py-2 flex items-center gap-2">
          <div class="grow">Visibility</div>
          <div class="flex gap-1 visibility-buttons">
            {#if UX_MODE !== "shpx"}
              <button class:visible={showItems} on:click={() => (showItems = !showItems)}>
                <LocationItemIcon />
              </button>
            {/if}
            <button class:visible={showCollections} on:click={() => (showCollections = !showCollections)}>
              <LocationCollectionIcon />
            </button>
            {#if UX_MODE !== "shpx"}
              <button class:visible={showTypes} on:click={() => (showTypes = !showTypes)}>
                <LocationTypeIcon />
              </button>
              <button class:visible={showNotes} on:click={() => (showNotes = !showNotes)}>
                <NotesIcon />
              </button>
            {/if}
            <button class:visible={showDims} on:click={() => (showDims = !showDims)}>
              <DimensionIcon />
            </button>
          </div>
        </div>
        <div class="px-4 py-2">
          <div class="flex items-center gap-2">
            <div class="grow">Filter Records</div>
            <div>
              <button class="visible" on:click={toggleRecordFilters}>
                {#if showRecordFilters}
                  <MinusIcon />
                {:else}
                  <PlusIcon />
                {/if}
              </button>
            </div>
          </div>
          {#if showRecordFilters}
            <div class="pl-4">
              <div class="flex items-center gap-3 border-b record-filters">
                {#if UX_MODE !== "shpx"}
                  <div class="tab" class:active={itemFilter} class:selected={filterType === "item"}>
                    {#if itemFilter}
                      <div class="active-dot" />
                    {/if}
                    <button on:click={() => (filterType = "item")}> Items </button>
                  </div>
                {/if}
                <div class="tab" class:active={collectionFilter} class:selected={filterType === "collection"}>
                  {#if collectionFilter}
                    <div class="active-dot" />
                  {/if}
                  <button on:click={() => (filterType = "collection")}> Openings </button>
                </div>
                {#if UX_MODE !== "shpx"}
                  <div class="tab" class:active={typeFilter} class:selected={filterType === "type"}>
                    {#if typeFilter}
                      <div class="active-dot" />
                    {/if}
                    <button on:click={() => (filterType = "type")}> Types </button>
                  </div>
                  <div class="tab" class:active={noteFilter} class:selected={filterType === "document_note"}>
                    {#if noteFilter}
                      <div class="active-dot" />
                    {/if}
                    <button on:click={() => (filterType = "document_note")}> Notes </button>
                  </div>
                {/if}
              </div>
              <div class="pt-1">
                <SelectInput
                  label="Filter by"
                  outerBorder={false}
                  labelWidth="5rem"
                  value={filter?.field}
                  on:input={(e) => updateFilter("field", e.detail.value)}
                  options={filterOptions} />
                {#if filter}
                  <SelectInput
                    label="Operator"
                    outerBorder={false}
                    labelWidth="5rem"
                    value={filter?.operator}
                    on:input={(e) => updateFilter("operator", e.detail.value)}
                    options={[
                      { label: "Is Equal To", value: "is_equal_to" },
                      { label: "Is Not Equal To", value: "is_not_equal_to" },
                    ]} />
                  <TextInput
                    label="Value"
                    outerBorder={false}
                    labelWidth="5rem"
                    value={filter?.value}
                    on:input={(e) => updateFilter("value", e.detail.value)} />
                {/if}
              </div>
            </div>
          {/if}
        </div>
        <div class="px-4 py-2">
          <div class="flex items-center gap-2">
            <div class="grow pb-2">Filter Marks by Category</div>
            <div>
              <button class="visible" on:click={toggleLocationFilter}>
                {#if showLocationFilter}
                  <MinusIcon />
                {:else}
                  <PlusIcon />
                {/if}
              </button>
            </div>
          </div>
          {#if showLocationFilter}
            <div class="px-4">
              <SelectInput
                label="Operator"
                outerBorder={false}
                labelWidth="4rem"
                value={locationFilter?.operator}
                options={[
                  { label: "None", value: undefined },
                  { label: "Is Equal To", value: "is_equal_to" },
                  { label: "Is Not Equal To", value: "is_not_equal_to" },
                ]}
                on:input={(e) => updateLocationFilter("operator", e.detail.value)} />
              {#if locationFilter?.operator}
                <TextInput
                  label="Value"
                  outerBorder={false}
                  labelWidth="4rem"
                  value={locationFilter?.value}
                  on:input={(e) => updateLocationFilter("value", e.detail.value)} />
              {/if}
            </div>
          {/if}
        </div>
      </div>
    </div>
  </div>
{/if}

<style lang="scss">
  .visibility-buttons {
    button {
      @apply text-gray-400 p-1 rounded;

      &.visible,
      &:hover {
        @apply text-black;
      }

      &:hover {
        @apply bg-gray-200;
      }
    }
  }

  .record-filters {
    .tab {
      @apply flex gap-0.5 items-center;

      .active-dot {
        @apply rounded-full w-1.5 h-1.5;
      }

      button {
        @apply text-gray-400 border-b border-transparent py-1;
      }

      &.active {
        .active-dot {
          @apply bg-gray-400;
        }

        &:hover,
        &.selected {
          .active-dot {
            @apply bg-black;
          }
        }
      }

      &:hover,
      &.selected {
        button {
          @apply border-black text-black;
        }
      }
    }
  }
</style>
